import { Button, Form, Input, Select, Space } from "antd";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router";

import ModelSelectOption from "@/components/Elements/ModelSelectOption";
import Revision from "@/components/InterventionTemplate/Revision";
import { TemplateRevisions } from "@/components/InterventionTemplate/TemplateRevisions";
import { INPUT_MAX_LENGTH } from "@/constants/Fields";
import { useInterventionType } from "@/entities/referential/referential-intervention-types";
import { useUpdate } from "@/hooks/queries";
import { useCreateRevision } from "@/hooks/queries/template";
import { AssetTypesSelect } from "@/modules/asset-types/asset-types-select";
import { validationErrorParser } from "@/modules/form-validation";
import SelectLangField from "@/modules/language/language-select-lang-field";
import { ModulesSelect } from "@/modules/modules/modules-select";
import QueryableSelect from "@/modules/queryable-select";
import { getSelectMultipleValues } from "@/modules/select";
import { SidePanelFooter, useSidePanel } from "@/modules/side-panel";
import { Alert } from "@/ui/alert";

// TODO DEV-8551 `Option` is deprecated. Please use `options` instead.
// eslint-disable-next-line @typescript-eslint/no-deprecated
const { Option } = Select;
const { useForm } = Form;

const InterventionTemplateEdit = ({ model }) => {
  const [form] = useForm();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { closeSidePanel } = useSidePanel();

  const templateRevisions = new TemplateRevisions(model);
  const isLatestRevision = templateRevisions.isLatest(model.id);
  const hasInterventions = model.interventions?.length > 0;
  const unauthorizedStatesEdition = ["Production"];
  const displayEditRevision =
    unauthorizedStatesEdition.includes(model.state) && isLatestRevision;

  const onClose = () => {
    form.resetFields();
    closeSidePanel();
  };

  const mutation = useUpdate("interventionTemplates", model.id);

  const defaultValues = {
    name: model.name,
    description: model.description,
    state: model.state,
    type: model.intervention_type_id,
    language: model.lang,
    organizations:
      model.organizations?.map(({ name, id }) => ({
        label: name,
        value: id,
      })) ?? [],
    asset_types:
      model.asset_types?.map(({ name, id }) => ({ label: name, value: id })) ??
      [],
    modules:
      model.modules?.map(({ name, id }) => ({ label: name, value: id })) ?? [],
    dynamicFlag: model.flags?.dynamic ?? false,
  };

  function onUpdate() {
    const data = {
      name: form.getFieldValue("name"),
      lang: form.getFieldValue("language"),
      description: form.getFieldValue("description"),
      intervention_type_id: form.getFieldValue("type"),
      organizations: getSelectMultipleValues(form, "organizations", []),
      asset_types: getSelectMultipleValues(form, "asset_types", []),
      modules: getSelectMultipleValues(form, "modules", []),
    };

    mutation.mutate(data, {
      onSuccess: function () {
        onClose();
      },
    });
  }

  const pathRegExp = new RegExp("/.*/[0-9]+");
  const invalidateKeys = pathRegExp.exec(location.pathname)
    ? [["interventionTemplates", "get"]]
    : [["interventionTemplates", "list"]];

  const mutationCreate = useCreateRevision(model.id, invalidateKeys);

  const { types } = useInterventionType();

  // TODO IMP-1217 fix require-await
  // eslint-disable-next-line require-await
  async function onCreateEditableRevision() {
    mutationCreate.mutate(model, {
      onSuccess: function (data) {
        onClose();
        const newRevisionId = data.data.data.id;
        navigate(`/intervention-templates/${newRevisionId}`);
      },
    });
  }

  function onGoToLatestRevision() {
    navigate(`/intervention-templates/${templateRevisions.latestRevision()}`);
    onClose();
  }

  function validationErrors() {
    return mutation.isError
      ? validationErrorParser(
          mutation.error,
          [
            "name",
            "lang",
            "description",
            "intervention_type_id",
            "organizations",
            "asset_types",
            "modules",
          ],
          "others"
        )
      : {};
  }

  function validation(item) {
    return {
      validateStatus: validationErrors()[item] ? "error" : "status",
      help: validationErrors()[item],
    };
  }

  const FORM_ID = "editInterventionTemplateForm";

  const ContentFragment = () => (
    <Form
      id={FORM_ID}
      form={form}
      layout="vertical"
      requiredMark
      initialValues={defaultValues}
      onFinish={onUpdate}
      role="form"
    >
      <Form.Item
        name="name"
        label={t("interventionTemplate:form.name.label")}
        {...validation("name")}
      >
        <Input
          placeholder={t("interventionTemplate:form.name.label")}
          rules={[{ required: true }]}
          data-testid="name"
          showCount
          maxLength={INPUT_MAX_LENGTH}
        />
      </Form.Item>
      <SelectLangField />
      <Form.Item
        name="description"
        label={t("interventionTemplate:form.description.label")}
        {...validation("description")}
      >
        <Input
          placeholder={t("interventionTemplate:form.description.label")}
          data-testid="description"
        />
      </Form.Item>
      <Form.Item
        name="type"
        label={t("interventionTemplate:form.type.label")}
        rules={[{ required: true }]}
        {...validation("type")}
      >
        <Select
          placeholder={t("interventionTemplate:form.type.label")}
          data-testid="type"
          disabled={hasInterventions}
        >
          {types.map(({ id, name }) => (
            <Option key={id} value={id}>
              {name}
            </Option>
          ))}
        </Select>
      </Form.Item>
      <QueryableSelect
        model="organizations"
        filterBy="name"
        fields={["id", "name"]}
        mode="multiple"
        optionFormat={ModelSelectOption}
        name="organizations"
        label={t("interventionTemplate:form.organizations.label")}
        placeholder={t("interventionTemplate:form.organizations.label")}
        {...validation("organizations")}
      />
      <AssetTypesSelect {...validation("asset_types")} />
      <ModulesSelect {...validation("modules")} />
      <Revision
        label={t("interventionTemplate:version.currentVersion")}
        version={model.version}
        state={model.state}
        stateType="interventiontemplate"
        additionalText={
          model.id && templateRevisions.isLatest(model.id)
            ? t("interventionTemplate:version.latest")
            : ""
        }
      />
      {validationErrors().others && <Form.Item {...validation("others")} />}
      <Alert
        display={mutation.isError}
        closable={{ afterClose: () => mutation.reset() }}
      />
      <SidePanelFooter>
        <Space>
          <Button onClick={onClose}>{t("global:cancel")}</Button>
          <Button
            loading={mutation.isPending}
            type="primary"
            data-testid="update"
            htmlType="submit"
            form={FORM_ID}
          >
            {t("global:update")}
          </Button>
        </Space>
      </SidePanelFooter>
    </Form>
  );

  const ExtraEditRevision = () => (
    <div style={{ marginTop: "30px" }}>
      <Button
        onClick={onCreateEditableRevision}
        loading={mutationCreate.isPending}
      >
        {t("interventionTemplate:version.createEditableRevision.button")}
      </Button>
    </div>
  );

  const LatestRevision = () => (
    <div style={{ marginTop: "30px" }}>
      <Button onClick={onGoToLatestRevision}>
        {t("interventionTemplate:version.latestRevision.button")}
      </Button>
    </div>
  );

  return (
    <>
      <ContentFragment />
      {displayEditRevision ? (
        <ExtraEditRevision />
      ) : !isLatestRevision ? (
        <LatestRevision />
      ) : null}
    </>
  );
};

InterventionTemplateEdit.propTypes = {
  model: PropTypes.object.isRequired,
};

export default InterventionTemplateEdit;
